/**
 * WhatsApp Templates Page
 *
 * Main page for managing WhatsApp templates and sequences
 * Integrates with the backend template management API
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Message as TemplateIcon,
  ViewTimeline as SequenceIcon
} from '@mui/icons-material';
import TemplateManagement from '../components/WhatsApp/TemplateManagement';
import SequenceManagement from '../components/WhatsApp/SequenceManagement';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface WhatsAppInbox {
  id: number;
  account_id: number;
  channel_type: string;
  channel_id?: number;
  whatsapp_channel_id?: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`whatsapp-tabpanel-${index}`}
      aria-labelledby={`whatsapp-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const WhatsAppTemplatesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);

  // Fetch WhatsApp channel for this account
  useEffect(() => {
    const fetchWhatsAppChannel = async () => {
      setLoading(true);
      try {
        const response = await api.get('/inboxes/');
        // API returns { inboxes: [...], total: N }
        const responseData = response.data;
        const inboxes: WhatsAppInbox[] = responseData?.inboxes || responseData || [];

        // Debug: Log all inboxes to see channel types
        console.log('📥 Fetched inboxes response:', responseData);
        console.log('📋 Inboxes array:', inboxes);

        if (Array.isArray(inboxes)) {
          console.log('📋 Channel types:', inboxes.map(i => ({ id: i.id, type: i.channel_type })));

          // Find WhatsApp channel (case-insensitive comparison).
          // Prefer a channel that's actually configured (has a whatsapp_channel_id) —
          // picking just the first "type=whatsapp" inbox is an ordering accident that
          // can land on an unconfigured/orphaned channel instead of the real one.
          const whatsappInboxes = inboxes.filter(
            (inbox) => inbox.channel_type?.toLowerCase() === 'whatsapp'
          );
          const whatsappInbox =
            whatsappInboxes.find((inbox) => inbox.whatsapp_channel_id) || whatsappInboxes[0];

          console.log('📱 Found WhatsApp inbox:', whatsappInbox);

          if (whatsappInbox) {
            // IMPORTANT: Backend expects the Channel.id (inbox ID), NOT the whatsapp_channel_id
            // The backend uses Channel.id to look up the channel, then accesses .whatsapp_config
            const resolvedChannelId = whatsappInbox.id;  // Use inbox ID, not whatsapp_channel_id
            console.log('🔑 Resolved channel ID (inbox ID):', resolvedChannelId);
            console.log('📱 WhatsApp channel ID (for reference):', whatsappInbox.whatsapp_channel_id);
            console.log('🏢 Account ID:', whatsappInbox.account_id);

            setChannelId(resolvedChannelId);
            // Get account ID from the inbox
            setAccountId(whatsappInbox.account_id);
          }
        } else {
          console.error('❌ Inboxes is not an array:', inboxes);
        }
        setError(null);
      } catch (err: any) {
        console.error('❌ Error fetching inboxes:', err);
        setError('Failed to load inboxes');
      } finally {
        setLoading(false);
      }
    };

    fetchWhatsAppChannel();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!channelId || !accountId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          No WhatsApp channel configured. Please configure a WhatsApp channel in Inboxes first to manage
          templates and sequences.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          WhatsApp Templates & Sequences
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage your WhatsApp message templates and automated sequences for marketing and customer engagement
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="whatsapp templates tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<TemplateIcon />}
            label="Message Templates"
            iconPosition="start"
            id="whatsapp-tab-0"
            aria-controls="whatsapp-tabpanel-0"
          />
          <Tab
            icon={<SequenceIcon />}
            label="Automated Sequences"
            iconPosition="start"
            id="whatsapp-tab-1"
            aria-controls="whatsapp-tabpanel-1"
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <TemplateManagement accountId={accountId} channelId={channelId} />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SequenceManagement accountId={accountId} />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default WhatsAppTemplatesPage;
